import Vue from "vue";
import { mapGetters, mapState } from "vuex";

import actionBarComponent from "@/components/child/actionBar/actionBar.vue";
import buttonIncluirComponent from "@/components/child/form/button/buttonIncluir.vue";
import checkboxComponent from "@/components/child/form/checkbox.vue";
import comboComponent from "@/components/child/form/combo.vue";
import dataTooltipComponent from "@/components/child/form/datatooltip.vue";
import decimalComponent from "@/components/child/form/decimal.vue";
import fieldsetComponent from "@/components/child/form/fieldset.vue";
import textareaComponent from "@/components/child/form/textarea.vue";
import gridComponent from "@/components/child/grid/grid.vue";
import { GridColumn, GridColumnType } from "@/components/child/grid/gridColumn";
import { Component, Watch } from "@/decorators";
import FormaFarmaceuticaModel from "@/models/formaFarmaceuticaModel";
import MoldeModel from "@/models/moldeModel";
import UsuarioModel from "@/models/usuarioModel";
import formaFarmaceuticaService from "@/services/formaFarmaceuticaService";
import moldeService from "@/services/moldeService";
import produtoService from "@/services/produto/produtoService";
import unidadeMedidaService from "@/services/unidadeMedidaService";
import { AppState, Getters, SessionActions } from "@/store/store";

import "../crud.scss";
import "./edit.scss";

@Component({
    components: {
        textareaComponent,
        checkboxComponent,
        comboComponent,
        dataTooltipComponent,
        decimalComponent,
        fieldsetComponent,
        gridComponent,
        buttonIncluirComponent,
        actionBarComponent,
    },
    computed: {
        ...mapState({
            usuarioLogado: (state: AppState) => state.session.usuarioLogado,
        }),
        ...mapGetters(["VALIDAR_PERMISSAO_SOMENTE_CONSULTA"] as Getters),
    },
})
export default class MoldeEditComponent extends Vue {
    // State computed props
    usuarioLogado: UsuarioModel;
    VALIDAR_PERMISSAO_SOMENTE_CONSULTA: (telaDescricao: string) => boolean;

    produtosOptions: Array<Object> = [];

    model = new MoldeModel();
    modelId = 0;

    somenteConsulta = false;
    produtoComboId = 0;
    subGrupo: number = null;
    quantidade = 0;
    produtoPadrao = true;

    private unidadeMedId: number = null;

    formasFarmaceuticaOptions: Array<Object> = [];

    gridColumnsFormaFarmaceutica: Array<GridColumn> = [
        new GridColumn("descricao", "Descri\u00e7\u00e3o", GridColumnType.String),
    ];

    private async load() {
        this.model = new MoldeModel();
        this.model.ativo = true;

        if (this.modelId > 0) {
            try {
                const data = await moldeService.get(this.modelId);

                this.model.updateFrom(data);
            } catch {
                this.$router.back();
            }
        }
    }

    private async loadFormasFarmaceutica() {
        try {
            this.formasFarmaceuticaOptions = await formaFarmaceuticaService.comboCached(this.usuarioLogado.franquiaId);
        } catch {}
    }

    private async save() {
        try {
            const isValid = await this.$validator.validateAll();
            if (isValid) {
                if (this.model.unidadeMedidaId == null) {
                    if (this.unidadeMedId != null) {
                        this.model.unidadeMedidaId = this.unidadeMedId;
                    } else {
                        const unidadeMedida = await unidadeMedidaService.comboCached(this.usuarioLogado.franquiaId);

                        const unidadeMl = unidadeMedida.filter(p => p.text == "ml");
                        if (unidadeMl.length > 0) {
                            this.model.unidadeMedidaId = unidadeMl[0].value;
                            this.unidadeMedId = unidadeMl[0].value;
                        }
                    }
                }

                const sucesso = await moldeService[!this.model.id ? "insert" : "update"](
                    this.model,
                    this.usuarioLogado.franquiaId,
                );

                if (sucesso) {
                    await this.$showInclusaoUpdate(this.model.id);
                    this.$router.back();
                }
            } else {
                this.$focusErrorField();
            }
        } catch {}
    }

    private cancel() {
        this.$router.back();
    }

    private onInclusaoFormaFarmaceutica() {
        const formaFarmaceuticaComponent = this.$el.querySelector("#formaFarmaceuticaId") as HTMLSelectElement;

        if (this.model.formaFarmaceuticaId > 0) {
            const alreadyExists = this.model.formasFarmaceuticas.some(
                item => item.id == this.model.formaFarmaceuticaId,
            );

            if (!alreadyExists) {
                const formaFarmaceuticaModel = new FormaFarmaceuticaModel();

                formaFarmaceuticaModel.id = this.model.formaFarmaceuticaId;
                formaFarmaceuticaModel.descricao = formaFarmaceuticaComponent.innerText;
                formaFarmaceuticaModel.itensPadroes = null;

                this.model.formasFarmaceuticas.push(formaFarmaceuticaModel);

                this.model.formaFarmaceuticaId = -1;
            }
        }
    }

    private onRemoveFormaFarmaceutica(model: FormaFarmaceuticaModel) {
        this.model.formasFarmaceuticas = this.model.formasFarmaceuticas.filter(p => p != model);
    }

    //@ts-ignore
    @Watch("unidadeMedId")
    private onUnidadeMedIdChanged() {
        if (this.unidadeMedId != this.model.unidadeMedidaId) {
            this.unidadeMedId = parseInt(this.model.unidadeMedidaId.toString());
            this.$showError(this.$t("__.ts.atencao"), this.$t("__.ts.sistApenasMl"));
        }
    }

    private async mounted() {
        await this.$store.dispatch(SessionActions.LOAD_USUARIO_LOGADO);

        this.somenteConsulta = this.VALIDAR_PERMISSAO_SOMENTE_CONSULTA("moldes");

        if (this.$route.params.id) {
            this.modelId = +this.$route.params.id;
        }

        const response = await Promise.all([
            formaFarmaceuticaService.comboCached(this.usuarioLogado.franquiaId),
            unidadeMedidaService.comboCached(this.usuarioLogado.franquiaId),
            produtoService.comboCached(this.usuarioLogado.franquiaId),
            this.load(),
        ]).withLoading();

        this.formasFarmaceuticaOptions = response[0];
        this.produtosOptions = response[2];

        const unidadeMl = response[1].filter(p => p.text == "ml");
        if (unidadeMl.length > 0) {
            this.model.unidadeMedidaId = unidadeMl[0].value;
            this.unidadeMedId = unidadeMl[0].value;
        }
    }
}
